/*
 * chatEMTFlow — scripted step engine for the self-serve in-chat Interac e-Transfer flow.
 * Pure JS (no React). ChatScreen.js drives it; SwipeCards.js renders the cards.
 * Mirrors chatSwipeFlow.js conventions: startEMTFlow() → initial state,
 * advanceEMTFlow(state, answer) → next state. Done flag signals flow completion.
 *
 * Steps: recipient-email → amount → message → preview → (success | cancelled)
 * The emt-preview card handles the API calls (preview + execute) internally,
 * reporting the result back via onAnswer so the flow can record the outcome.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function stepAcceptsEMTFreeText(step) {
  return false; // all EMT input goes through the inline card inputs, not the chat text bar
}

export function startEMTFlow() {
  return {
    nextState: { step: 'recipient-email', recipientEmail: null, amount: null, message: null },
    botMessage: "I can walk you through sending an Interac e-Transfer right now.\n\nWho are you sending money to? Enter their email address.",
    card: {
      type: 'emt-free-text',
      field: 'recipientEmail',
      label: 'Recipient email',
      placeholder: 'email@example.com',
      inputType: 'email',
      validate: 'email',
      cta: 'Next',
    },
  };
}

export function advanceEMTFlow(state, answer) {
  const { step } = state;

  switch (step) {
    case 'recipient-email': {
      const email = String(answer || '').trim();
      if (!EMAIL_RE.test(email)) {
        return {
          nextState: state,
          botMessage: `That doesn't look like a valid email address. Please try again.`,
          card: {
            type: 'emt-free-text',
            field: 'recipientEmail',
            label: 'Recipient email',
            placeholder: 'email@example.com',
            inputType: 'email',
            validate: 'email',
            cta: 'Next',
          },
        };
      }
      return {
        nextState: { ...state, step: 'amount', recipientEmail: email },
        botMessage: `Sending to **${email}**. How much would you like to send?`,
        card: {
          type: 'emt-free-text',
          field: 'amount',
          label: 'Amount',
          placeholder: '0.00',
          inputType: 'number',
          prefix: '$',
          validate: 'positive-number',
          cta: 'Next',
        },
      };
    }

    case 'amount': {
      const amount = parseFloat(answer);
      if (!(amount > 0)) {
        return {
          nextState: state,
          botMessage: 'Please enter a positive amount.',
          card: {
            type: 'emt-free-text',
            field: 'amount',
            label: 'Amount',
            placeholder: '0.00',
            inputType: 'number',
            prefix: '$',
            validate: 'positive-number',
            cta: 'Next',
          },
        };
      }
      return {
        nextState: { ...state, step: 'message', amount },
        botMessage: `Got it — **$${amount.toFixed(2)}**. Add a note for the recipient? (optional)`,
        card: {
          type: 'emt-free-text',
          field: 'message',
          label: 'Note',
          placeholder: 'e.g. "Your half of rent"',
          inputType: 'text',
          skippable: true,
          cta: 'Next',
        },
      };
    }

    case 'message': {
      const message = typeof answer === 'string' && answer.trim() ? answer.trim() : null;
      return {
        nextState: { ...state, step: 'preview', message },
        botMessage: "Here's your transfer summary — review and confirm:",
        card: {
          type: 'emt-preview',
          recipientEmail: state.recipientEmail,
          amount: state.amount,
          message,
        },
      };
    }

    case 'preview': {
      // answer is either { confirmed: true, referenceNumber } or { confirmed: false } (cancelled)
      if (!answer?.confirmed) {
        return {
          nextState: { ...state, step: 'cancelled' },
          botMessage: "Transfer cancelled — your money stayed put. Let me know if you need anything else.",
          card: null,
          done: true,
        };
      }
      return {
        nextState: { ...state, step: 'success', referenceNumber: answer.referenceNumber },
        botMessage: `Your e-Transfer of **$${state.amount?.toFixed(2)}** to ${state.recipientEmail} has been sent.`,
        card: {
          type: 'emt-success',
          recipientEmail: state.recipientEmail,
          amount: state.amount,
          referenceNumber: answer.referenceNumber,
        },
        done: true,
      };
    }

    default:
      return { nextState: state, botMessage: '', card: null, done: true };
  }
}
